import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import PropTypes from "prop-types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Button from "./ui/Button";
import Typography from "./ui/Typography";
import { groupReleasesByCalendarWeek } from "../utils/groupReleasesByCalendarWeek";
import { cn } from "../utils/classNames";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { trackEvent } from "../utils/analytics";

const MIN_FIRST_WEEK_SHOWS = 6;
const subscribeToCalendarDay = () => () => {};
const getServerCalendarDay = () => null;

function getLocalCalendarDay(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isGroupCompletedBeforeDay(group, day) {
  return Boolean(
    group?.endDate
    && typeof day === "string"
    && /^\d{4}-\d{2}-\d{2}$/.test(day)
    && group.endDate < day
  );
}

export function resolveInterstitialAfterGroupIndex(groups = [], referenceDay = "") {
  if (groups.length === 0) return -1;

  const activeDatedIndices = groups.reduce((indices, group, index) => {
    if (group.endDate && !isGroupCompletedBeforeDay(group, referenceDay)) {
      indices.push(index);
    }
    return indices;
  }, []);

  if (activeDatedIndices.length === 0) {
    return groups[0]?.shows.length >= MIN_FIRST_WEEK_SHOWS
      ? 0
      : Math.min(1, groups.length - 1);
  }

  const firstActiveIndex = activeDatedIndices[0];
  if (groups[firstActiveIndex]?.shows.length >= MIN_FIRST_WEEK_SHOWS) {
    return firstActiveIndex;
  }

  return activeDatedIndices[1] ?? firstActiveIndex;
}

const DateGroupedReleases = ({
  releases,
  renderReleases,
  interstitial = null,
  referenceDay = "",
  className,
}) => {
  const groups = useMemo(() => groupReleasesByCalendarWeek(releases), [releases]);
  const groupKeys = useMemo(() => groups.map((group) => group.key), [groups]);
  const [observedActiveKey, setObservedActiveKey] = useState(groupKeys[0] || null);
  const [arrivalKey, setArrivalKey] = useState(null);
  const visitorDay = useSyncExternalStore(subscribeToCalendarDay, getLocalCalendarDay, getServerCalendarDay);
  const sectionRefs = useRef(new Map());
  const arrivalTimeoutRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const activeKey = groupKeys.includes(observedActiveKey) ? observedActiveKey : groupKeys[0] || null;
  const activeIndex = Math.max(0, groupKeys.indexOf(activeKey));
  const interstitialAfterGroupIndex = resolveInterstitialAfterGroupIndex(groups, referenceDay);

  useEffect(() => () => {
    if (arrivalTimeoutRef.current) clearTimeout(arrivalTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined" || groups.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);
        const nextKey = visible[0]?.target?.dataset?.weekKey;
        if (nextKey) setObservedActiveKey(nextKey);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );

    for (const group of groups) {
      const section = sectionRefs.current.get(group.key);
      if (section) observer.observe(section);
    }

    return () => observer.disconnect();
  }, [groups]);

  const navigateToGroup = (key, inputType) => {
    const section = sectionRefs.current.get(key);
    if (!section) return;

    trackEvent("homepage_week_jump", {
      destination_week: key,
      input_type: inputType,
    });
    setObservedActiveKey(key);
    if (!prefersReducedMotion) {
      setArrivalKey(key);
      if (arrivalTimeoutRef.current) clearTimeout(arrivalTimeoutRef.current);
      arrivalTimeoutRef.current = setTimeout(() => {
        setArrivalKey(null);
        arrivalTimeoutRef.current = null;
      }, 700);
    }
    section.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
    section.querySelector("[data-week-heading]")?.focus({ preventScroll: true });
  };

  if (groups.length === 0) return null;

  return (
    <div className={cn("space-y-10", className)}>
      <nav aria-label="Jump to release week" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Typography variant="caption" className="font-semibold text-muted-foreground">
            Browse by release week
          </Typography>
          {groups.length > 1 ? <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              disabled={activeIndex === 0}
              onClick={() => navigateToGroup(groupKeys[activeIndex - 1], "previous")}
              aria-label={activeIndex === 0 ? "No previous release week" : `Go to ${groups[activeIndex - 1].label}`}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              disabled={activeIndex >= groups.length - 1}
              onClick={() => navigateToGroup(groupKeys[activeIndex + 1], "next")}
              aria-label={activeIndex >= groups.length - 1 ? "No next release week" : `Go to ${groups[activeIndex + 1].label}`}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div> : null}
        </div>

        <div className="overflow-x-auto pb-1 pt-2">
          <div className="flex min-w-max gap-2">
            {groups.map((group) => {
              const isActive = group.key === activeKey;
              return (
                <Button
                  key={group.key}
                  type="button"
                  variant={isActive ? "filter-active" : "filter"}
                  size="sm"
                  className="min-h-11 shrink-0"
                  onClick={() => navigateToGroup(group.key, "week_chip")}
                  aria-current={isActive ? "location" : undefined}
                >
                  {group.label}
                </Button>
              );
            })}
          </div>
        </div>
      </nav>

      <div className="space-y-12">
        {groups.map((group, groupIndex) => {
          const isRecentlyPremiered = groupIndex === 0
            && visitorDay
            && isGroupCompletedBeforeDay(group, visitorDay);
          const slots = [];
          slots.push(
            <React.Fragment key={`${group.key}-all`}>
              {renderReleases(group.shows, group.key)}
            </React.Fragment>,
          );
          if (interstitial && groupIndex === interstitialAfterGroupIndex) {
            slots.push(<div key={`${group.key}-interstitial`} className="pt-2">{interstitial}</div>);
          }

          return (
            <section
              key={group.key}
              ref={(node) => {
                if (node) sectionRefs.current.set(group.key, node);
                else sectionRefs.current.delete(group.key);
              }}
              data-week-key={group.key}
              data-arriving={arrivalKey === group.key ? "true" : undefined}
              aria-labelledby={`release-week-${group.key}`}
              className="release-week-section scroll-mt-40 space-y-6"
            >
              <div className="release-week-heading-row flex items-baseline justify-between gap-4 border-b border-border/60 pb-3">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Typography
                    as="h2"
                    variant="h3"
                    id={`release-week-${group.key}`}
                    data-week-heading
                    tabIndex={-1}
                    className="font-semibold text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {group.label}
                  </Typography>
                  {isRecentlyPremiered ? (
                    <Typography variant="caption" className="font-semibold text-muted-foreground">
                      Recently premiered
                    </Typography>
                  ) : null}
                </div>
                <Typography variant="caption" className="shrink-0 text-muted-foreground">
                  {group.shows.length} {group.shows.length === 1 ? "show" : "shows"}
                </Typography>
              </div>
              <div className="space-y-8">{slots}</div>
            </section>
          );
        })}

      </div>
    </div>
  );
};

DateGroupedReleases.propTypes = {
  releases: PropTypes.arrayOf(PropTypes.object).isRequired,
  renderReleases: PropTypes.func.isRequired,
  interstitial: PropTypes.node,
  referenceDay: PropTypes.string,
  className: PropTypes.string,
};

export default DateGroupedReleases;
